'use client';

import { useState, useEffect, useCallback } from 'react';

// Available assistant icons
export const ASSISTANT_ICONS = {
  pisrs: { id: 'pisrs', label: 'PISRS', description: 'Logotip PISRS', colorful: false },
  scale: { id: 'scale', label: 'Tehtnica', description: 'Simbol pravice', colorful: false },
  pilcrow: { id: 'pilcrow', label: 'Paragraf (¶)', description: 'Pravni paragraf', colorful: false },
  scrollText: { id: 'scrollText', label: 'Zvitek', description: 'Dokument', colorful: false },
  bookText: { id: 'bookText', label: 'Knjiga', description: 'Pravna literatura', colorful: false },
  library: { id: 'library', label: 'Knjižnica', description: 'Zbirka zakonov', colorful: false },
  gavel: { id: 'gavel', label: 'Kladivo', description: 'Sodniško kladivo', colorful: false },
  // Colorful icons
  money: { id: 'money', label: 'Denar', description: 'Zelena', colorful: true },
  clock: { id: 'clock', label: 'Ura', description: 'Modra', colorful: true },
  chart: { id: 'chart', label: 'Graf', description: 'Vijolična', colorful: true },
  star: { id: 'star', label: 'Zvezda', description: 'Rumena', colorful: true },
  bell: { id: 'bell', label: 'Zvonec', description: 'Roza', colorful: true },
  checkCircle: { id: 'checkCircle', label: 'Kljukica', description: 'Turkizna', colorful: true },
  shield: { id: 'shield', label: 'Ščit', description: 'Gradient', colorful: true },
  flame: { id: 'flame', label: 'Plamen', description: 'Oranžna', colorful: true },
  tribonijan: { id: 'tribonijan', label: 'Tribonijan', description: 'Logo', colorful: true },
} as const;

export type AssistantIconId = keyof typeof ASSISTANT_ICONS;

// Dark theme presets (Geist-inspired color palette)
export const DARK_THEMES = {
  // Neutrals
  black: {
    id: 'black',
    label: 'Čista črna',
    description: 'OLED črna',
    background: 'oklch(0 0 0)',
    card: 'oklch(0.1 0 0)',
    muted: 'oklch(0.15 0 0)',
  },
  charcoal: {
    id: 'charcoal',
    label: 'Oglje',
    description: 'Temno siva',
    background: 'oklch(0.12 0 0)',
    card: 'oklch(0.16 0 0)',
    muted: 'oklch(0.2 0 0)',
  },
  graphite: {
    id: 'graphite',
    label: 'Grafitna',
    description: 'Srednje temna',
    background: 'oklch(0.18 0 0)',
    card: 'oklch(0.22 0 0)',
    muted: 'oklch(0.26 0 0)',
  },
  // Blues
  blue: {
    id: 'blue',
    label: 'Temno modra',
    description: 'Privzeta',
    background: 'oklch(0.129 0.042 264.695)',
    card: 'oklch(0.208 0.042 265.755)',
    muted: 'oklch(0.279 0.041 260.031)',
  },
  navy: {
    id: 'navy',
    label: 'Mornarsko modra',
    description: 'Globoka modra',
    background: 'oklch(0.15 0.06 260)',
    card: 'oklch(0.2 0.055 262)',
    muted: 'oklch(0.28 0.05 264)',
  },
  // Greens
  forest: {
    id: 'forest',
    label: 'Gozdna zelena',
    description: 'Naravna',
    background: 'oklch(0.14 0.04 150)',
    card: 'oklch(0.19 0.038 152)',
    muted: 'oklch(0.26 0.035 155)',
  },
  emerald: {
    id: 'emerald',
    label: 'Smaragdna',
    description: 'Živa zelena',
    background: 'oklch(0.13 0.05 165)',
    card: 'oklch(0.18 0.048 163)',
    muted: 'oklch(0.25 0.045 160)',
  },
  // Teals
  teal: {
    id: 'teal',
    label: 'Turkizna',
    description: 'Modro-zelena',
    background: 'oklch(0.14 0.045 195)',
    card: 'oklch(0.19 0.042 193)',
    muted: 'oklch(0.26 0.04 190)',
  },
  // Purples
  purple: {
    id: 'purple',
    label: 'Vijolična',
    description: 'Kreativna',
    background: 'oklch(0.14 0.05 300)',
    card: 'oklch(0.19 0.048 298)',
    muted: 'oklch(0.26 0.045 295)',
  },
  grape: {
    id: 'grape',
    label: 'Grozdna',
    description: 'Temno vijolična',
    background: 'oklch(0.13 0.055 310)',
    card: 'oklch(0.18 0.052 308)',
    muted: 'oklch(0.25 0.048 305)',
  },
  // Warm tones
  rose: {
    id: 'rose',
    label: 'Vrtnica',
    description: 'Topla roza',
    background: 'oklch(0.14 0.04 350)',
    card: 'oklch(0.19 0.038 348)',
    muted: 'oklch(0.26 0.035 345)',
  },
  wine: {
    id: 'wine',
    label: 'Vinska',
    description: 'Temno rdeča',
    background: 'oklch(0.13 0.045 15)',
    card: 'oklch(0.18 0.042 12)',
    muted: 'oklch(0.25 0.04 10)',
  },
  amber: {
    id: 'amber',
    label: 'Jantarna',
    description: 'Topla oranžna',
    background: 'oklch(0.14 0.04 60)',
    card: 'oklch(0.19 0.038 58)',
    muted: 'oklch(0.26 0.035 55)',
  },
} as const;

export type DarkThemeId = keyof typeof DARK_THEMES;

// Light theme presets (Geist color scales - lots of options!)
export const LIGHT_THEMES = {
  // === NEVTRALNE ===
  white: {
    id: 'white',
    label: 'Bela',
    description: 'Čista bela',
    background: '#ffffff',
    card: '#fafafa',
    muted: '#f4f4f5',
  },
  snow: {
    id: 'snow',
    label: 'Snežna',
    description: 'Mehka bela',
    background: '#fafafa',
    card: '#f5f5f5',
    muted: '#e5e5e5',
  },
  ivory: {
    id: 'ivory',
    label: 'Slonokoščena',
    description: 'Topla bela',
    background: '#fffff0',
    card: '#fafaf5',
    muted: '#f0f0e8',
  },
  silver: {
    id: 'silver',
    label: 'Srebrna',
    description: 'Svetlo siva',
    background: '#f8f9fa',
    card: '#f1f3f5',
    muted: '#e9ecef',
  },
  // === MODRE ===
  skyBlue: {
    id: 'skyBlue',
    label: 'Nebesno modra',
    description: 'Svetla modra',
    background: '#f0f9ff',
    card: '#e0f2fe',
    muted: '#bae6fd',
  },
  babyBlue: {
    id: 'babyBlue',
    label: 'Baby modra',
    description: 'Nežna modra',
    background: '#eff6ff',
    card: '#dbeafe',
    muted: '#bfdbfe',
  },
  ice: {
    id: 'ice',
    label: 'Ledena',
    description: 'Hladna modra',
    background: '#f0faff',
    card: '#e6f4ff',
    muted: '#d1ebff',
  },
  azure: {
    id: 'azure',
    label: 'Azurna',
    description: 'Morska modra',
    background: '#ecfeff',
    card: '#cffafe',
    muted: '#a5f3fc',
  },
  // === ZELENE ===
  mint: {
    id: 'mint',
    label: 'Metina',
    description: 'Sveža zelena',
    background: '#f0fdf4',
    card: '#dcfce7',
    muted: '#bbf7d0',
  },
  sage: {
    id: 'sage',
    label: 'Žajbljeva',
    description: 'Mehka zelena',
    background: '#f1f8f4',
    card: '#e2f0e8',
    muted: '#c8e0d0',
  },
  honeydew: {
    id: 'honeydew',
    label: 'Medena rosa',
    description: 'Bleda zelena',
    background: '#f0fff4',
    card: '#e6ffed',
    muted: '#d1fae0',
  },
  pistachio: {
    id: 'pistachio',
    label: 'Pistacija',
    description: 'Topla zelena',
    background: '#f4fdf0',
    card: '#e8fadc',
    muted: '#d4f4c0',
  },
  // === TURKIZNE ===
  aqua: {
    id: 'aqua',
    label: 'Aqua',
    description: 'Modro-zelena',
    background: '#f0fdfa',
    card: '#ccfbf1',
    muted: '#99f6e4',
  },
  seafoam: {
    id: 'seafoam',
    label: 'Morska pena',
    description: 'Nežna turkizna',
    background: '#effefb',
    card: '#d5fcf3',
    muted: '#b2f5ea',
  },
  // === VIJOLIČNE ===
  lavender: {
    id: 'lavender',
    label: 'Sivka',
    description: 'Nežna vijolična',
    background: '#faf5ff',
    card: '#f3e8ff',
    muted: '#e9d5ff',
  },
  lilac: {
    id: 'lilac',
    label: 'Bezeg',
    description: 'Svetla vijolična',
    background: '#fdf4ff',
    card: '#fae8ff',
    muted: '#f5d0fe',
  },
  periwinkle: {
    id: 'periwinkle',
    label: 'Zimzelen',
    description: 'Modro-vijolična',
    background: '#f5f3ff',
    card: '#ede9fe',
    muted: '#ddd6fe',
  },
  wisteria: {
    id: 'wisteria',
    label: 'Glicinija',
    description: 'Mehka lila',
    background: '#f8f5ff',
    card: '#f0eaff',
    muted: '#e4d8ff',
  },
  // === ROZA ===
  blush: {
    id: 'blush',
    label: 'Rdeča',
    description: 'Nežna roza',
    background: '#fdf2f8',
    card: '#fce7f3',
    muted: '#fbcfe8',
  },
  rose: {
    id: 'rose',
    label: 'Vrtnica',
    description: 'Klasična roza',
    background: '#fff1f2',
    card: '#ffe4e6',
    muted: '#fecdd3',
  },
  peony: {
    id: 'peony',
    label: 'Potonika',
    description: 'Živa roza',
    background: '#fef1f6',
    card: '#fde4ed',
    muted: '#fbc8db',
  },
  sakura: {
    id: 'sakura',
    label: 'Sakura',
    description: 'Češnjev cvet',
    background: '#fef7f9',
    card: '#feecf0',
    muted: '#fdd8e1',
  },
  bubblegum: {
    id: 'bubblegum',
    label: 'Žvečilka',
    description: 'Sladka roza',
    background: '#fef0f5',
    card: '#fde2ec',
    muted: '#fbc5d8',
  },
  // === TOPLE (PEACH/CORAL) ===
  peach: {
    id: 'peach',
    label: 'Breskev',
    description: 'Topla oranžna',
    background: '#fff7ed',
    card: '#ffedd5',
    muted: '#fed7aa',
  },
  apricot: {
    id: 'apricot',
    label: 'Marelica',
    description: 'Mehka oranžna',
    background: '#fff8f0',
    card: '#ffefdb',
    muted: '#ffe0bd',
  },
  coral: {
    id: 'coral',
    label: 'Koralna',
    description: 'Roza-oranžna',
    background: '#fff5f5',
    card: '#ffe8e8',
    muted: '#ffd4d4',
  },
  // === RUMENE ===
  cream: {
    id: 'cream',
    label: 'Kremna',
    description: 'Topla rumena',
    background: '#fffef5',
    card: '#fffde8',
    muted: '#fffacd',
  },
  vanilla: {
    id: 'vanilla',
    label: 'Vanilija',
    description: 'Mehka rumena',
    background: '#fffef8',
    card: '#fffcea',
    muted: '#fff8d6',
  },
  lemon: {
    id: 'lemon',
    label: 'Limonina',
    description: 'Svetla rumena',
    background: '#fefce8',
    card: '#fef9c3',
    muted: '#fef08a',
  },
  champagne: {
    id: 'champagne',
    label: 'Šampanjec',
    description: 'Elegantna',
    background: '#fdfbf5',
    card: '#faf6eb',
    muted: '#f5eed8',
  },
  // === POSEBNE ===
  sand: {
    id: 'sand',
    label: 'Peščena',
    description: 'Topla nevtralna',
    background: '#faf9f6',
    card: '#f5f3ef',
    muted: '#e8e4dc',
  },
  linen: {
    id: 'linen',
    label: 'Lanena',
    description: 'Naravna',
    background: '#faf9f7',
    card: '#f5f3f0',
    muted: '#e9e5df',
  },
  parchment: {
    id: 'parchment',
    label: 'Pergament',
    description: 'Starinska',
    background: '#fcfaf5',
    card: '#f8f4eb',
    muted: '#f0e8d8',
  },
  cotton: {
    id: 'cotton',
    label: 'Bombaž',
    description: 'Čista mehka',
    background: '#fcfcfc',
    card: '#f8f8f8',
    muted: '#f0f0f0',
  },
} as const;

export type LightThemeId = keyof typeof LIGHT_THEMES;

// Design skins (smer vizualnega jezika) — glej /design skill.
// Skin spreminja naglas (--brand) + dekorativno ozadje; barvno ozadje ostane ločeno.
export const SKINS = {
  lex: {
    id: 'lex',
    label: 'Lex Machina',
    description: 'Moderni AI · lapis naglas',
    brand: 'oklch(0.55 0.16 262)',
    defaultBg: 'glow',
  },
  chamber: {
    id: 'chamber',
    label: 'Chamber',
    description: 'Temni oder · zlat naglas',
    brand: 'oklch(0.74 0.12 80)',
    defaultBg: 'aurora',
  },
} as const;

export type SkinId = keyof typeof SKINS;

// Inline žetoni naglasa po skinu + načinu (light/dark).
// Nastavljamo inline (kot barvne presete), ker Tailwind v4 izpušča
// top-level [data-skin] pravila iz globals.css.
export const SKIN_TOKENS: Record<SkinId, Record<'light' | 'dark', Record<string, string>>> = {
  lex: {
    light: { '--brand': 'oklch(0.55 0.16 262)', '--brand-foreground': 'oklch(0.99 0.003 250)' },
    dark: { '--brand': 'oklch(0.68 0.17 262)', '--brand-foreground': 'oklch(0.18 0.042 265)' },
  },
  chamber: {
    light: {
      '--brand': 'oklch(0.58 0.10 75)', '--brand-foreground': 'oklch(0.99 0.01 88)',
      '--primary': 'oklch(0.58 0.10 75)', '--primary-foreground': 'oklch(0.99 0.01 88)',
      '--ring': 'oklch(0.58 0.10 75)',
    },
    dark: {
      '--brand': 'oklch(0.82 0.12 85)', '--brand-foreground': 'oklch(0.18 0.02 75)',
      '--primary': 'oklch(0.82 0.12 85)', '--primary-foreground': 'oklch(0.18 0.02 75)',
      '--ring': 'oklch(0.82 0.12 85)', '--border': 'oklch(0.82 0.12 85 / 0.18)',
    },
  },
};

// Žetoni, ki jih kdaj nastavi chamber — ob lex jih moramo POČISTITI (inline)
const SKIN_RESETTABLE = ['--primary', '--primary-foreground', '--ring', '--border'];

// Dekorativni vzorci ozadja
export const BG_PATTERNS = {
  none: { id: 'none', label: 'Brez', description: 'Čisto ozadje' },
  glow: { id: 'glow', label: 'Žar', description: 'Mehak naglasni žar' },
  grid: { id: 'grid', label: 'Mreža', description: 'Pikčasta mreža' },
  aurora: { id: 'aurora', label: 'Aurora', description: 'Barvni žarki' },
} as const;

export type BgPatternId = keyof typeof BG_PATTERNS;

export interface AppSettings {
  assistantIcon: AssistantIconId;
  darkTheme: DarkThemeId;
  lightTheme: LightThemeId;
  skin: SkinId;
  bgPattern: BgPatternId;
}

const DEFAULT_SETTINGS: AppSettings = {
  assistantIcon: 'pisrs',
  darkTheme: 'blue',
  lightTheme: 'white',
  skin: 'lex',
  bgPattern: 'glow',
};

const STORAGE_KEY = 'tribonianus-settings';

/**
 * Hook for managing app settings with localStorage persistence
 */
export function useSettings() {
  const [settings, setSettings] = useState<AppSettings>(DEFAULT_SETTINGS);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load settings from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setSettings({ ...DEFAULT_SETTINGS, ...parsed });
      }
    } catch (e) {
      console.error('Failed to load settings:', e);
    }
    setIsLoaded(true);
  }, []);

  // Apply theme CSS variables when settings change
  useEffect(() => {
    if (!isLoaded) return;

    const darkTheme = DARK_THEMES[settings.darkTheme];
    const lightTheme = LIGHT_THEMES[settings.lightTheme];
    const root = document.documentElement;

    // CSS variables that we override for themes
    const themeVars = [
      '--background', '--card', '--popover', '--muted',
      '--secondary', '--accent', '--sidebar', '--sidebar-accent'
    ];

    // Apply the appropriate theme based on current mode
    const applyTheme = () => {
      if (root.classList.contains('dark')) {
        // Dark mode
        root.style.setProperty('--background', darkTheme.background);
        root.style.setProperty('--card', darkTheme.card);
        root.style.setProperty('--popover', darkTheme.card);
        root.style.setProperty('--muted', darkTheme.muted);
        root.style.setProperty('--secondary', darkTheme.muted);
        root.style.setProperty('--accent', darkTheme.muted);
        root.style.setProperty('--sidebar', darkTheme.card);
        root.style.setProperty('--sidebar-accent', darkTheme.muted);
      } else {
        // Light mode
        root.style.setProperty('--background', lightTheme.background);
        root.style.setProperty('--card', lightTheme.card);
        root.style.setProperty('--popover', lightTheme.card);
        root.style.setProperty('--muted', lightTheme.muted);
        root.style.setProperty('--secondary', lightTheme.muted);
        root.style.setProperty('--accent', lightTheme.muted);
        root.style.setProperty('--sidebar', lightTheme.card);
        root.style.setProperty('--sidebar-accent', lightTheme.muted);
      }

      // Skin naglas (--brand + chamber primary/ring/border) — inline
      const mode = root.classList.contains('dark') ? 'dark' : 'light';
      // Najprej počisti morebitne chamber-specifične override (za vrnitev na lex)
      for (const v of SKIN_RESETTABLE) root.style.removeProperty(v);
      const tokens = SKIN_TOKENS[settings.skin]?.[mode] ?? SKIN_TOKENS.lex[mode];
      for (const [k, val] of Object.entries(tokens)) root.style.setProperty(k, val);
    };

    applyTheme();

    // Watch for dark/light mode changes
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.attributeName === 'class') {
          applyTheme();
        }
      }
    });

    observer.observe(root, { attributes: true });

    return () => observer.disconnect();
  }, [settings.darkTheme, settings.lightTheme, settings.skin, isLoaded]);

  // Apply design skin + decorative background pattern as data-attributes
  useEffect(() => {
    if (!isLoaded) return;
    const root = document.documentElement;
    root.setAttribute('data-skin', settings.skin);
    root.setAttribute('data-bg', settings.bgPattern);
  }, [settings.skin, settings.bgPattern, isLoaded]);

  // Save settings to localStorage
  const saveSettings = useCallback((newSettings: Partial<AppSettings>) => {
    setSettings(prev => {
      const updated = { ...prev, ...newSettings };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      } catch (e) {
        console.error('Failed to save settings:', e);
      }
      return updated;
    });
  }, []);

  const setAssistantIcon = useCallback((icon: AssistantIconId) => {
    saveSettings({ assistantIcon: icon });
  }, [saveSettings]);

  const setDarkTheme = useCallback((theme: DarkThemeId) => {
    saveSettings({ darkTheme: theme });
  }, [saveSettings]);

  const setLightTheme = useCallback((theme: LightThemeId) => {
    saveSettings({ lightTheme: theme });
  }, [saveSettings]);

  // Set skin and auto-pick its recommended decorative background
  const setSkin = useCallback((skin: SkinId) => {
    saveSettings({ skin, bgPattern: SKINS[skin].defaultBg as BgPatternId });
  }, [saveSettings]);

  const setBgPattern = useCallback((bgPattern: BgPatternId) => {
    saveSettings({ bgPattern });
  }, [saveSettings]);

  return {
    settings,
    isLoaded,
    setAssistantIcon,
    setDarkTheme,
    setLightTheme,
    setSkin,
    setBgPattern,
  };
}
